import { FieldErrors, UseFormProps, ValidationRule } from 'react-hook-form';
import React, { KeyboardEvent } from 'react';
import { GridProps } from '@mui/material';
import { FieldError, ValidateResult } from 'react-hook-form/dist/types';
import { conf } from 'config';

export interface InputProps {
  variant: 'outlined';
  fullWidth: boolean;
}

export interface FormProps {
  inputProps?: InputProps;
  rowProps?: GridProps;
  fieldName?: string;
  label?: string;
  disabled?: boolean;
  required?: boolean;
}

export interface ValidationProps {
  error: boolean;
  helperText?: string | React.ReactElement;
}

export const defaultFormProps: UseFormProps<any> = {
  reValidateMode: 'onChange',
  mode: 'all',
  criteriaMode: 'all',
};

export interface InputProps {
  variant: 'outlined';
  fullWidth: boolean;
}

export const defaultInputProps: InputProps = {
  fullWidth: true,
  variant: 'outlined',
};

export const defaultTinyMCEProps = {
  apiKey: conf.tinyMCEApiKey || '',
  init: {
    height: 300,
    plugins: [
      'anchor',
      'autolink',
      'charmap',
      'codesample',
      'codesample',
      'emoticons',
      'image',
      'link',
      'media',
      'searchreplace',
      'visualblocks',
      'wordcount',
      'code',
    ],
    toolbar:
      'undo redo | ' +
      'blocks fontsize forecolor | ' +
      'bold italic underline strikethrough removeformat | ' +
      'link image media table | ' +
      'align lineheight | ' +
      'emoticons charmap | ' +
      'code',
    toolbar_mode: 'wrap',
    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
    inline: false,
    branding: false,
    automatic_uploads: true,
    paste_data_images: true,
  },
};

export const ACH_REGEXP = /^([0-9]{6}|[0-9]{9})$/;
export const FREEFORM_TEXT_MAX_LENGTH = 300;
export const EMAIL_MAX_LENGTH = 254;
export const EMAIL_REGEXP = /^([A-Za-z0-9_\-.+])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,})$/;
export const DEFAULT_POSTAL_CODE_REGEXP = /^[a-zA-Z0-9]{1,20}$/;
export const NUMBER_WHOLE_REGEXP = /^\d+$/;
export const NUMBER_DECIMAL_REGEXP = /^\d*\.\d+$/;
export const NUMBER_WHOLE_PLUS_DECIMAL_REGEXP = /^\d*(\.\d+)?$/;

export const LOGIN_USERNAME_FIELD_ID = 'login-username-field';
export const LOGIN_SECTION_ID = 'login-section';

export const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please reload the page and try again';

export const getRequiredInputMessage = (label: string): string => `Please input ${label}`;

export const getRequiredSelectMessage = (label: string): string => `Please select ${label}`;

export const getLimitExceedInputMessage = (maxLength: number): string =>
  `Input cannot be longer than ${maxLength} character(s)`;

export const getMaxLengthValidationRule = (maxLength: number): ValidationRule<number> => ({
  value: maxLength,
  message: getLimitExceedInputMessage(maxLength),
});

export const getMinLengthValidationRule = (minLength: number): ValidationRule<number> => ({
  value: minLength,
  message: `Input cannot be less than ${minLength} character(s)`,
});

export const getRequiredValidationRule = (
  label: string,
  ruleRequired = true,
  isSelect = false
): ValidationRule<boolean> => ({
  value: ruleRequired,
  message: isSelect ? getRequiredSelectMessage(label) : getRequiredInputMessage(label),
});

export const getValidationError = (label: string, errors?: FieldErrors): boolean => {
  return !!errors && errors.hasOwnProperty(label) && errors[label] !== undefined;
};

export const getValidationHelperText = (label: string, errors?: FieldErrors): string =>
  !!errors && errors.hasOwnProperty(label) && errors[label] !== undefined ? (errors[label] as any).message : '';

export const getValidationErrorProps = (error?: FieldError): ValidationProps =>
  error
    ? {
        error: true,
        helperText: error?.message,
      }
    : {
        error: false,
        helperText: undefined,
      };

export const getValidationProps = (label: string, errors?: FieldErrors): ValidationProps => {
  const values = label.split('.').reduce((nextObject: any, key: string) => {
    if (nextObject) {
      return nextObject[key];
    }
  }, errors);

  return !!errors && values
    ? {
        error: true,
        helperText: values.message || undefined,
      }
    : {
        error: false,
        helperText: undefined,
      };
};

export const getArrayFieldValidationProps = <T,>(
  label: keyof T,
  fieldIndex: number,
  errors?: FieldErrors
): ValidationProps => {
  const errorObj =
    !!errors && errors.hasOwnProperty(label) && Array.isArray(errors[label])
      ? (errors[label] as any[])[fieldIndex]
      : undefined;

  return errorObj
    ? {
        error: true,
        helperText: errorObj.value.message || '',
      }
    : { error: false, helperText: '' };
};

export const validateEmailValue = (
  value: string,
  disabled = false,
  optional = false
): ValidateResult | Promise<ValidateResult> => {
  if (disabled) {
    return true;
  }
  if (value) {
    if (value.length > EMAIL_MAX_LENGTH) {
      return getLimitExceedInputMessage(EMAIL_MAX_LENGTH);
    } else if (!EMAIL_REGEXP.test(value)) {
      return 'Please input valid email';
    }
    return true;
  } else {
    return optional || getRequiredInputMessage('email');
  }
};

export const preventDecimalCharsInput = (e: KeyboardEvent): void => {
  if (e.key === '.' || e.key === 'e') {
    e.preventDefault();
  }
};

export const getUnsavedChangesPromptMessage = (): string =>
  'Your changes have not been saved. Click "Ok" to continue to the application homepage or "Cancel" to go back to the application and save your changes.';
